import React, { Component } from 'react'
import queryString from 'query-string'
import Papa from 'papaparse'
import { UploadCSV } from './'
import { 
  initUploadCSV, 
  convertCSVToObjects, 
  verifyUpload, 
  createTeachers,
  createClasses,
  createStudents, 
  createUserAssessments,
  createUsernameCSVs,
} from '../../helpers/assessment/uploadCSV'
import { db } from '../../config/firebaseconfig';

/**
 * Manages logic and state for the Upload CSV functionality.
 * Allows super admin to upload a csv of student responses.
 * On this route: /assessment/upload/school/
 */

class UploadCSVContainer extends Component {
  state = {
    assessment: null,
    district: null,
    school: null,
    loading: true,
    loadingError: '',
    processedStudents: 0,
    processing: false,
    status: '',
    totalStudents: 0,
    uploadedDataBool: false,
    uploadError: '',
    usernameCSVsReady: false,
    verifying: false,
    verified: false,
    verifyWarnings: null,
    verifyErrors: null,
  }
  
  componentDidMount() {
    const { match, location } = this.props
    const { recordType, recordId } = match.params
    const query = queryString.parse(location.search)
    const assessmentId = query.a
    if (recordType === 'school') {
      // load school, district, and assessment objects.
      this.setState({loading: true})
      initUploadCSV(recordId, assessmentId)
        .then((data) => {
          const { school, district, assessment } = data
          this.setState({
            school,
            district,
            assessment,
            loading: false,
            loadingError: ''
          })
        })
        .catch(error => this.setState({loading: false, loadingError: error}))
    } else {
      this.setState({
        loadingError: `This view does not yet support 
                       uploading for anything other than 
                       schools.`
      })
    }
  }

  // accepts the input of a csv and converts it into an array
  handleUpload = ({target}) => {
    const file = target.files[0]
    Papa.parse(file, {
      complete: (results => {
        this.uploadedData = convertCSVToObjects(results.data)
        this.setState({uploadedDataBool: true})
     }),
      error: (error => this.setState({uploadError: 'Could not parse uploaded file.'}))
    })
  }

  // handles verifying the input csv and generating error
  // and warning messages
  handleVerify = () => {
    const { uploadedData } = this
    const { school, assessment } = this.state
    const { location } = this.props
    const query = queryString.parse(location.search)
    const schoolYear = query.sy
    this.setState({verifying: true})
    verifyUpload(uploadedData, school, schoolYear, assessment)
      .then(results => {
        const { 
          fieldErrors,
          fieldWarnings,
          studentErrors,
          studentWarnings,
          teacherErrors,
          teacherWarnings  
        } = results
        this.setState({
          verifying: false,
          verified: true,
          verifyWarnings: {
            fieldWarnings,
            studentWarnings,
            teacherWarnings,
          },
          verifyErrors: {
            fieldErrors,
            studentErrors,
            teacherErrors,
          },
        })
      })
      .catch(error => {
        this.setState({
          verified: false,
          verifying: false,
          verifyErrors: [error],
      })})
  }

  // handle processing the verified data
  handleProcess = () => {
    const { uploadedData, processCSVs, storeBackup } = this
    const { school, assessment, } = this.state
    const { location, match } = this.props
    const { recordId } = match.params
    const query = queryString.parse(location.search)
    const schoolYear = query.sy
    const assessmentId = query.a
    let backup = {
      active: true,
      assessmentId, 
      dateUploaded: Date.now(),
      recordId,  
      schoolYear, 
      uploadedData,
    }
    this.setState({status: 'Creating teachers...', processing: true})
    createTeachers(uploadedData, school, schoolYear, assessmentId, assessment)
      .then(createdTeachers => {
        this.setState({status: 'Creating classes...'})
        backup.createdTeachers = createdTeachers
        return createClasses(uploadedData, school, schoolYear)
      })
      .then(createdClasses => {
        this.setState({status: 'Creating students...'})
        backup.createdClasses = createdClasses
        return createStudents(uploadedData, school, schoolYear, assessmentId, assessment)
      })
      .then(createdStudents => {
        this.setState({status: 'Processing student responses...'})
        backup.createdStudents = createdStudents.filter(stu => stu.data).map(stu => stu.data)
        backup.studentErrors = createdStudents.filter(stu => stu.error).map(stu => stu.error)
        if (backup.studentErrors.length > 0) {
          this.studentErrors = backup.studentErrors
          this.setState({hasStudentErrors: true})
        }
        return createUserAssessments(uploadedData, school, schoolYear, assessmentId, this.setState.bind(this))
      })
      .then(createdUserAssessments => {
        this.setState({processing: false, status: 'Completed processing!'})
        backup.createdUserAssessments = createdUserAssessments
        return Promise.all([processCSVs(backup), storeBackup(backup)])
      })
      .catch(err => console.log(err))
  }

  // stores backup data at end of successful upload
  storeBackup = (backup) => {
    // change backup data to only be arrays of ids
    const createdUserAssessments = (
      backup.createdUserAssessments.map(uA => uA.userAssessmentId)
    );
    const backupUpload = {
      ...backup,
      createdClasses: backup.createdClasses.map(cl => cl.id),
      createdStudents: backup.createdStudents.map(st => st.id),
      createdTeachers: backup.createdTeachers.map(tc => tc.id),
      createdUserAssessments,
    }
    console.log('backupUpload', backupUpload)
    return db.collection('legacyUploadBackups').add(backupUpload)
  }


  // processes backup data so usernames created can be output
  // as CSVs
  processCSVs = (backup) => {
    const { match, location } = this.props
    const { recordId } = match.params
    const query = queryString.parse(location.search)
    const assessmentId = query.a
    const year = query.sy
    return createUsernameCSVs(backup, recordId, assessmentId, year)
      .then(({students, teachers}) => {
        this.studentsCSV = students
        this.teachersCSV = teachers
        this.setState({usernameCSVsReady: true})
      })
      .catch(err => console.log(err))
  }

  links = () => {
    const { recordId, recordType } = this.props.match.params
    const { search } = this.props.location
    const BASE_URL_CSV = '/assessment/upload/username-csv/'
    const BASE_URL_BACKUP = '/assessment/upload/backups/'
    const ROUTE = `${recordType}/${recordId}`
    return {
      linkToUsernameCSVs: `${BASE_URL_CSV}${ROUTE}${search}`,
      linkToBackups: `${BASE_URL_BACKUP}${ROUTE}${search}`,
    }
  }
  
  render() {
    const { 
      handleUpload, 
      handleVerify,
      handleProcess,
      uploadedData,
      studentsCSV,
      teachersCSV,
      links,
    } = this
    const { 
      assessment,
      district, 
      hasStudentErrors,
      loading, 
      loadingError, 
      processedStudents,
      processing,
      school, 
      status,
      totalStudents,
      uploadedDataBool,
      uploadError,
      verifying,
      verifyWarnings,
      verifyErrors,
      verified,
      usernameCSVsReady,
    } = this.state
    const { location, } = this.props
    const { linkToUsernameCSVs, linkToBackups, } = links()

    return (
      <UploadCSV 
        handleUpload={handleUpload} 
        handleVerify={handleVerify}
        handleProcess={handleProcess}
        assessment={assessment}
        district={district} 
        linkToBackups={linkToBackups}
        linkToUsernameCSVs={linkToUsernameCSVs}
        loading={loading}
        loadingError={loadingError} 
        location={location}
        processedStudents={processedStudents}
        processing={processing}
        school={school} 
        status={status}
        studentsCSV={studentsCSV}
        // studentErrors={studentErrors}
        teachersCSV={teachersCSV}
        totalStudents={totalStudents}
        uploadedData={uploadedData}
        uploadedDataBool={uploadedDataBool}
        uploadError={uploadError}
        usernameCSVsReady={usernameCSVsReady}
        verifying={verifying}
        verifyWarnings={verifyWarnings}
        verifyErrors={verifyErrors}
        verified={verified}
      />
    )
  }
}

export default UploadCSVContainer


// WEBPACK FOOTER //
// ./src/components/Assessment/UploadCSVContainer.js